<script setup>
import { onMounted, ref, computed } from "vue"
import { useRoute } from "vue-router"

import Layout from "../../layouts/main.vue"
import PageHeader from "@/components/page-header"

import Lottie from "@/components/widgets/lottie.vue"
import animationData from "@/components/widgets/msoeawqm.json"
import PersianDate from "persian-date"

import store from "../../state/store"

const allstoresettlements = computed(() => store.state.allStoreSettlements)

const title = "لیست پرداختی ها"
const items = ref([
  {
    text: "فروشگاه ها",
    href: "/dashboard/stores",
  },
  {
    text: "لیست پرداختی ها",
    active: true,
  },
])

const perPage = ref(10)
const sortField = ref("updated_at")
const sortDirection = ref("desc")

const route = useRoute()
const storeId = route.params.storeId

const formattedPrices = (value) => {
  return new Intl.NumberFormat("fa-IR").format(value)
}

const getAllStoreSettlements = async (url = null) => {
  try {
    await store.dispatch("getAllStoreSettlements", {
      storeId,
      url,
      per_page: perPage.value,
      sort_field: sortField.value,
      sort_direction: sortDirection.value,
    })
  } catch (error) {
    console.error(error)
    return null
  }
}

onMounted(async () => {
  getAllStoreSettlements()
})

const toPersianDateTime = (date) => {
  const [datePart, timePart] = date.split(" ")
  const [year, month, day] = datePart.split("-")
  const [hour, minute, second] = timePart.split(":")
  const gregorianDate = new Date(year, month - 1, day, hour, minute, second)
  const persianDate = new PersianDate(gregorianDate)
  return persianDate.format("YYYY/MM/DD HH:mm:ss")
}

function getForPage(ev, link) {
  ev.preventDefault()
  if (!link.url || link.active) {
    return
  }

  getAllStoreSettlements(link.url)
}

const defaultOptions = ref({
  animationData: animationData,
})
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="items" />
    <b-row>
      <b-col lg="12">
        <b-card no-body id="storeCreditorDetailList">
          <b-card-header class="border-bottom-dashed">
            <b-row class="g-4 align-items-center">
              <b-col sm>
                <div>
                  <h5 class="card-title mb-0">
                    لیست پرداختی های یادینو به
                    <strong
                      v-if="
                        !allstoresettlements.loading &&
                        allstoresettlements.data.length
                      "
                    >
                      <mark>
                        {{ allstoresettlements.data[0].storeName }}
                      </mark>
                    </strong>
                  </h5>
                </div>
              </b-col>
              <b-col sm="auto">
                <div class="d-flex flex-wrap align-items-start gap-2"></div>
              </b-col>
            </b-row>
          </b-card-header>

          <b-card-body class="border-bottom-dashed border-bottom">
            <b-row class="g-3">
              <b-col xl="7"> </b-col>

              <b-col xl="5">
                <b-row class="g-3">
                  <b-col sm="6"> </b-col>

                  <b-col sm="6">
                    <select
                      @change="getAllStoreSettlements(null)"
                      v-model="perPage"
                      class="form-select mb-3"
                      aria-label="Default select example"
                    >
                      <option value="" selected>نمایش تعداد ردیف</option>
                      <option value="2">2</option>
                      <option value="5">5</option>
                      <option value="10">10</option>
                      <option value="20">20</option>
                      <option value="30">30</option>
                      <option value="50">50</option>
                    </select>
                  </b-col>
                </b-row>
              </b-col>
            </b-row>
          </b-card-body>
          <b-card-body>
            <div>
              <div class="table-responsive table-card mb-1">
                <table class="table align-middle" id="storeSettlementsTable">
                  <thead class="table-light text-muted">
                    <tr>
                      <th class="sort" data-sort="amount_deposited">
                        مبلغ واریز شده
                      </th>
                      <th class="sort" data-sort="commission">مبلغ کمیسیون</th>
                      <th class="sort" data-sort="create_at">زمان ثبت تسویه</th>
                    </tr>
                  </thead>

                  <tbody
                    class="list"
                    v-if="
                      allstoresettlements.loading ||
                      !allstoresettlements.data.length
                    "
                  >
                    <tr>
                      <td colspan="8">
                        <div class="text-center">
                          <b-spinner
                            v-if="allstoresettlements.loading"
                            variant="dark"
                            style="width: 3rem; height: 3rem"
                          ></b-spinner>
                          <div v-else>
                            <lottie
                              class="avatar-xl"
                              colors="primary:#121331,secondary:#08a88a"
                              :options="defaultOptions"
                              :height="75"
                              :width="75"
                            />
                            <h5 class="mt-2">هیچ پرداختی وجود ندارد!</h5>
                          </div>
                        </div>
                      </td>
                    </tr>
                  </tbody>

                  <tbody class="list" v-else>
                    <tr
                      v-for="(item, index) of allstoresettlements.data"
                      :key="index"
                    >
                      <td>{{ formattedPrices(item.amountDeposited) }}</td>

                      <td class="fw-medium">
                        {{ formattedPrices(item.commission) }}
                      </td>
                      <td class="create_at">
                        {{ toPersianDateTime(item.createSettlementDateTime) }}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div
                class="d-flex justify-content-end"
                v-if="allstoresettlements.total > allstoresettlements.limit"
              >
                <div class="pagination-wrap hstack gap-2" style="display: flex">
                  <ul class="pagination listjs-pagination mb-0">
                    <li
                      v-for="(link, i) of allstoresettlements.links"
                      :key="i"
                      class="page-item"
                      :class="{
                        disabled: !link.url,
                        active: link.active || !link.url,
                      }"
                    >
                      <span
                        class="page-link"
                        v-if="link.active"
                        v-html="link.label"
                      ></span>

                      <a
                        v-else
                        href="#"
                        @click="getForPage($event, link)"
                        class="page-link"
                        v-html="link.label"
                      >
                      </a>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </b-card-body>
        </b-card>
      </b-col>
    </b-row>
  </Layout>
</template>
